import {z} from 'zod';
import {congestionAreaClusterIdSchema, organizationIdSchema} from './id';

/**
 * The widest ceiling the column can hold: `numeric(10, 3)` leaves seven digits before the
 * point. Bounded at the edge so an accidental extra zero is a validation error a reader
 * can act on, rather than a `numeric field overflow` from Postgres.
 */
export const MAX_GUARD_MW = 9_999_999;

/**
 * A named set of congestion areas that share one MW ceiling.
 *
 * **Nobody assigns areas to a group through this tool.** There is no endpoint and no
 * screen for membership: the first client's grouping is known, and it is applied with SQL
 * against the database (`docs/congestion-area-clusters.md`). What the tool owes is the
 * model, the enforcement and a way to *see* the grouping — building an editor for a set
 * that changes about once a year would be the expensive half of a feature nobody asked
 * for. An area in no group is the common case and is not a misconfiguration.
 *
 * **A group belongs to one organization**, like the areas in it. ElaadNL's own clusters
 * span a DSO rather than a tenant, and `docs/open-questions.md` §25 argues a ceiling that
 * stops at a tenant boundary can be walked around by two organizations sharing a
 * congestion point. That is accepted for now, decided on 2026-09-30: every area in the
 * first client's G1B is one organization's, so the two readings cannot yet disagree, and
 * a cross-tenant guard would have to leak one tenant's consumption into another's
 * refusal. The composite foreign key from `congestion_area` is what holds the boundary.
 */
export const congestionAreaClusterSchema = z.object({
  id: congestionAreaClusterIdSchema,
  organizationId: organizationIdSchema,
  /**
   * The short label the grid is actually discussed in — `G1B`. Optional because ElaadNL's
   * own cluster export has one without it, and a group is identified by its name either
   * way.
   */
  code: z.string().trim().min(1).max(32).nullable(),
  name: z.string().trim().min(1).max(200),
  /**
   * The ceiling in MW over every area in the group, per quarter-hour. `null` is a group
   * that binds nothing — a grouping kept for the sake of showing it.
   *
   * Read as **power**, not energy: open question §20, settled our way on 2026-09-30. The
   * check is per quarter against the sum of the areas' requested flex, never a volume
   * integrated over the day.
   */
  guardMw: z.number().nonnegative().max(MAX_GUARD_MW).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type CongestionAreaCluster = z.infer<typeof congestionAreaClusterSchema>;

/**
 * A group as a list shows it: the group plus how many areas sit in it, so a reader can
 * tell a grouping that is doing something from one that was created and never populated.
 */
export const congestionAreaClusterSummarySchema = congestionAreaClusterSchema.extend({
  areaCount: z.number().int().nonnegative(),
});

export type CongestionAreaClusterSummary = z.infer<typeof congestionAreaClusterSummarySchema>;

/**
 * What an organisation administrator may change about a cluster: its ceiling, and
 * nothing else.
 *
 * **Membership stays out of the tool.** Which areas are in a cluster is a fact about the
 * grid that ElaadNL decide and that changes about once a year; the ceiling is a number
 * the organisation tunes. Only the second earns a screen.
 *
 * **Zero and `null` both mean "no ceiling", and are stored as `null`.** A reader who
 * types `0` means the same thing as one who clears the field, and two spellings of one
 * state is how a later "is there a ceiling?" gets written two ways and disagrees with
 * itself. Normalised here, at the edge, so nothing downstream has to know.
 */
export const configureCongestionAreaClusterSchema = z
  .object({
    guardMw: z
      .number()
      .nonnegative()
      .max(MAX_GUARD_MW)
      .nullable()
      .transform(value => (value ? value : null)),
  })
  .strict();

export type ConfigureCongestionAreaCluster = z.infer<typeof configureCongestionAreaClusterSchema>;
